import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../firebase';
import { collection, onSnapshot, query, orderBy, where, doc, Timestamp } from 'firebase/firestore';

const REFRESH_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/refreshLeadData';
const ADS_REFRESH_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/refreshAdsData';
const MAX_WINDOW_DAYS = 92; // mirrors the backend cap

// Quality badges: text label is the primary signal (color-blind safe) — color
// is only reinforcement, with strong lightness differences between states.
const QUALITY_META = {
  qualified:  { label: 'Qualified',  color: '#4ade80', bg: 'rgba(22,163,74,.12)' },
  spam:       { label: 'Spam',       color: '#f87171', bg: 'rgba(220,38,38,.12)' },
  'low-value':{ label: 'Low-value',  color: '#fbbf24', bg: 'rgba(251,191,36,.10)' },
  unscored:   { label: 'Unscored',   color: 'var(--text-muted)', bg: 'var(--bg-secondary)' },
};
const QUALITY_ORDER = ['qualified', 'low-value', 'spam', 'unscored'];

const ymd = (d) => d.toISOString().slice(0, 10);

function presetRange(preset) {
  const now = new Date();
  const today = ymd(now);
  if (preset === 'month') return { start: today.slice(0, 8) + '01', end: today };
  if (preset === '30') return { start: ymd(new Date(Date.now() - 29 * 86400000)), end: today };
  if (preset === '90') return { start: ymd(new Date(Date.now() - 89 * 86400000)), end: today };
  return null;
}

function fmtDateTime(ts) {
  if (!ts) return '—';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
           d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return '—'; }
}

function fmtDuration(sec) {
  if (typeof sec !== 'number' || sec <= 0) return '—';
  const m = Math.floor(sec / 60), s = sec % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}

function pct(n, total) { return total ? Math.round((n / total) * 100) : 0; }

function fmtMoney(v) {
  return typeof v === 'number'
    ? '$' + v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: v < 100 ? 2 : 0 })
    : '—';
}

// Labeled horizontal bar for the claimed-vs-verified comparison. The label and
// count carry the meaning; bar length is relative to the largest value shown.
function CompareBar({ label, value, max, strong, note }) {
  const w = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(190px, 240px) 1fr', gap: 10, alignItems: 'center', marginBottom: 8 }}>
      <div style={{ fontSize: 12, color: strong ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: strong ? 700 : 400, textAlign: 'right' }}>
        {label}{note ? <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> {note}</span> : null}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: `${w}%`, height: 20, borderRadius: 4,
          background: strong ? '#4338ca' : 'var(--bg-secondary)',
          border: strong ? 'none' : '1px solid var(--border)' }} />
        <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{Math.round(value * 10) / 10}</span>
      </div>
    </div>
  );
}

// Attribution chip — text-first so the state reads without color.
function AttributionChip({ lead }) {
  const id = lead.gclid ? 'gclid' : lead.fbclid ? 'fbclid' : lead.msclkid ? 'msclkid' : null;
  return id
    ? <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'rgba(79,70,229,.15)', color: '#a5b4fc' }}>✓ {id}</span>
    : <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>unattributed</span>;
}

function QualityBadge({ score }) {
  const m = QUALITY_META[score] || QUALITY_META.unscored;
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: m.bg, color: m.color }}>{m.label}</span>;
}

// Simple horizontal split bar with in-bar counts (labels carry the meaning;
// the two segments differ in lightness, not just hue).
function SplitBar({ a, b, aLabel, bLabel }) {
  const total = a + b;
  if (!total) return <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No data</div>;
  return (
    <div style={{ display: 'flex', height: 26, borderRadius: 6, overflow: 'hidden', fontSize: 11, fontWeight: 600 }}>
      {a > 0 && (
        <div style={{ width: `${pct(a, total)}%`, minWidth: 40, background: '#3730a3', color: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', padding: '0 4px' }}>
          {aLabel} {a} ({pct(a, total)}%)
        </div>
      )}
      {b > 0 && (
        <div style={{ width: `${pct(b, total)}%`, minWidth: 40, background: 'var(--bg-secondary)', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', padding: '0 4px', border: '1px solid var(--border)' }}>
          {bLabel} {b} ({pct(b, total)}%)
        </div>
      )}
    </div>
  );
}

export default function WhatConverts() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [preset, setPreset] = useState('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [leads, setLeads] = useState([]);
  const [syncMeta, setSyncMeta] = useState(null);
  const [ads, setAds] = useState(null); // clients/{id}/adsSync/latest (Windsor pull)
  const [adsWarning, setAdsWarning] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Feed controls
  const [typeFilter, setTypeFilter] = useState('');
  const [qualityFilter, setQualityFilter] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('timestamp');
  const [sortDir, setSortDir] = useState('desc');
  const [openId, setOpenId] = useState(null);

  // Only clients with Call Clarity Reports enabled appear — same flag that
  // gates the monthly Clarity report. Others never reach this dashboard.
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'clients'), orderBy('businessName')),
      snap => setClients(snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(c => !c.isArchived && c.clarity_enabled === true && c.callscaler_api_key)),
      err => console.error('clients snapshot error:', err)
    );
    return unsub;
  }, []);

  const range = useMemo(() => {
    if (preset !== 'custom') return presetRange(preset);
    if (customStart && customEnd && customStart <= customEnd) return { start: customStart, end: customEnd };
    return null;
  }, [preset, customStart, customEnd]);

  const windowDays = range ? Math.round((Date.parse(range.end) - Date.parse(range.start)) / 86400000) + 1 : 0;

  // Live leads for the selected client + window.
  useEffect(() => {
    setLeads([]);
    if (!clientId || !range) return;
    const startTs = Timestamp.fromDate(new Date(range.start + 'T00:00:00'));
    const endTs = Timestamp.fromDate(new Date(range.end + 'T23:59:59.999'));
    const q = query(
      collection(db, 'clients', clientId, 'leads'),
      where('timestamp', '>=', startTs),
      where('timestamp', '<=', endTs),
      orderBy('timestamp', 'desc')
    );
    const unsub = onSnapshot(q,
      snap => setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err => { console.error('leads snapshot error:', err); setError('Could not load leads: ' + err.message); }
    );
    return unsub;
  }, [clientId, range?.start, range?.end]);

  useEffect(() => {
    setSyncMeta(null);
    setAds(null);
    setAdsWarning('');
    if (!clientId) return;
    const unsubMeta = onSnapshot(doc(db, 'clients', clientId, 'leadSync', 'latest'),
      snap => setSyncMeta(snap.exists() ? snap.data() : null),
      () => {});
    const unsubAds = onSnapshot(doc(db, 'clients', clientId, 'adsSync', 'latest'),
      snap => setAds(snap.exists() ? snap.data() : null),
      () => {});
    return () => { unsubMeta(); unsubAds(); };
  }, [clientId]);

  const handleRefresh = async () => {
    if (!clientId || !range) return;
    setError('');
    setNotice('');
    if (windowDays > MAX_WINDOW_DAYS) { setError(`Date range too large (${windowDays} days; max ${MAX_WINDOW_DAYS}).`); return; }
    setRefreshing(true);
    try {
      if (!auth.currentUser) throw new Error('You are signed out — sign in again.');
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(REFRESH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ clientId, startDate: range.start, endDate: range.end }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Refresh failed (${res.status})`);
      setNotice(`Pulled ${data.calls} calls + ${data.forms} form submissions from CallScaler (${data.leadsUpserted} leads stored).`);

      // Windsor ad-spend pull — independent of the lead pull, so a failure
      // here (missing key, no linked account) is a warning, not an error.
      setAdsWarning('');
      const selected = clients.find(c => c.id === clientId);
      if (selected?.windsorAdsAccountId) {
        try {
          const adsRes = await fetch(ADS_REFRESH_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({ clientId, startDate: range.start, endDate: range.end }),
          });
          const adsData = await adsRes.json();
          if (!adsRes.ok) throw new Error(adsData.error || `Ads refresh failed (${adsRes.status})`);
        } catch (adsErr) {
          setAdsWarning('Google Ads data not refreshed: ' + adsErr.message);
        }
      }
    } catch (err) {
      setError(err.message);
    }
    setRefreshing(false);
  };

  // ---- Aggregates -----------------------------------------------------------
  const calls = useMemo(() => leads.filter(l => l.leadType === 'call'), [leads]);
  const forms = useMemo(() => leads.filter(l => l.leadType === 'form'), [leads]);

  const attribution = useMemo(() => ({
    call: { matched: calls.filter(l => l.attributionComplete).length, total: calls.length },
    form: { matched: forms.filter(l => l.attributionComplete).length, total: forms.length },
  }), [calls, forms]);

  // Channel mix over time — daily buckets up to 31 days, weekly beyond.
  const trend = useMemo(() => {
    if (!range || !leads.length) return [];
    const weekly = windowDays > 31;
    const buckets = new Map();
    const startMs = Date.parse(range.start + 'T00:00:00');
    for (const l of leads) {
      const d = l.timestamp?.toDate ? l.timestamp.toDate() : null;
      if (!d) continue;
      let key;
      if (weekly) {
        const weekIdx = Math.floor((d.getTime() - startMs) / (7 * 86400000));
        key = ymd(new Date(startMs + weekIdx * 7 * 86400000));
      } else {
        key = ymd(d);
      }
      const b = buckets.get(key) || { key, calls: 0, forms: 0 };
      b[l.leadType === 'form' ? 'forms' : 'calls']++;
      buckets.set(key, b);
    }
    return [...buckets.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [leads, range?.start, windowDays]);

  // Quality cross-tab: by channel, then by campaign (top 8 by volume).
  const qualityByChannel = useMemo(() => {
    const rows = [{ label: 'Calls', items: calls }, { label: 'Forms', items: forms }];
    return rows.map(r => ({
      label: r.label,
      total: r.items.length,
      counts: Object.fromEntries(QUALITY_ORDER.map(q => [q, r.items.filter(l => (l.qualityScore || 'unscored') === q).length])),
    }));
  }, [calls, forms]);

  const qualityByCampaign = useMemo(() => {
    const map = new Map();
    for (const l of leads) {
      const key = l.campaign || l.source || 'Unknown';
      const row = map.get(key) || { label: key, total: 0, counts: Object.fromEntries(QUALITY_ORDER.map(q => [q, 0])) };
      row.total++;
      row.counts[QUALITY_ORDER.includes(l.qualityScore) ? l.qualityScore : 'unscored']++;
      map.set(key, row);
    }
    return [...map.values()].sort((a, b) => b.total - a.total).slice(0, 8);
  }, [leads]);

  // Hub-verified side of the claimed-vs-verified comparison.
  const hubQualified = useMemo(() => ({
    total: leads.filter(l => l.qualityScore === 'qualified').length,
    withGclid: leads.filter(l => l.qualityScore === 'qualified' && l.gclid).length,
  }), [leads]);

  const adsWindowMatches = ads?.window && range
    && ads.window.startDate === range.start && ads.window.endDate === range.end;

  // ---- Feed sorting/filtering ----------------------------------------------
  const feed = useMemo(() => {
    let rows = leads;
    if (typeFilter) rows = rows.filter(l => l.leadType === typeFilter);
    if (qualityFilter) rows = rows.filter(l => (l.qualityScore || 'unscored') === qualityFilter);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      rows = rows.filter(l => [l.source, l.medium, l.campaign, l.keyword, l.callerName, l.formName, l.aiCategory]
        .some(v => String(v || '').toLowerCase().includes(s)));
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sortKey === 'timestamp') {
        return dir * ((a.timestamp?.toMillis?.() || 0) - (b.timestamp?.toMillis?.() || 0));
      }
      return dir * String(a[sortKey] || '').localeCompare(String(b[sortKey] || ''));
    });
  }, [leads, typeFilter, qualityFilter, search, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'timestamp' ? 'desc' : 'asc'); }
  };

  const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const client = clients.find(c => c.id === clientId);
  const maxTrend = Math.max(1, ...trend.map(t => t.calls + t.forms));

  const card = { background: 'var(--bg-card, var(--bg-primary))', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 };
  const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 13, borderTop: '1px solid var(--border)', verticalAlign: 'top' };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 16px' }}>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>WhatConverts</h1>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Unified lead feed — CallScaler calls + form submissions with session attribution. Internal only.
        Only clients with Call Clarity Reports enabled appear here.
      </p>

      {/* Controls */}
      <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Client</label>
          <select value={clientId} onChange={e => setClientId(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', minWidth: 220 }}>
            <option value="">Select a client…</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.businessName}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Date range</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['month', 'This month'], ['30', 'Last 30 days'], ['90', 'Last 90 days'], ['custom', 'Custom']].map(([val, label]) => (
              <button key={val} onClick={() => setPreset(val)}
                style={{ padding: '8px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer', border: '1px solid', fontWeight: preset === val ? 700 : 400,
                  background: preset === val ? '#1e1b4b' : 'var(--bg-secondary)',
                  borderColor: preset === val ? '#6366f1' : 'var(--border)',
                  color: preset === val ? '#c7d2fe' : 'var(--text-secondary)' }}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {preset === 'custom' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Start</label>
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                style={{ padding: '7px 10px', borderRadius: 8, background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>End</label>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                style={{ padding: '7px 10px', borderRadius: 8, background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }} />
            </div>
          </div>
        )}
        <button onClick={handleRefresh} disabled={!clientId || !range || refreshing}
          style={{ padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: clientId && range && !refreshing ? 'pointer' : 'not-allowed',
            background: '#3730a3', color: '#e0e7ff', border: '1px solid #6366f1', opacity: !clientId || !range || refreshing ? 0.5 : 1 }}>
          {refreshing ? 'Pulling from CallScaler…' : '↻ Refresh lead data'}
        </button>
        {syncMeta?.syncedAt && (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Last pull: {fmtDateTime(syncMeta.syncedAt)} ({syncMeta.window?.startDate} → {syncMeta.window?.endDate})
          </span>
        )}
      </div>

      {error && <div style={{ ...card, borderColor: '#f87171', color: '#f87171', fontSize: 13 }}>{error}</div>}
      {notice && <div style={{ ...card, borderColor: '#4ade80', color: '#4ade80', fontSize: 13 }}>{notice}</div>}
      {adsWarning && <div style={{ ...card, borderColor: '#fbbf24', color: '#fbbf24', fontSize: 13 }}>{adsWarning}</div>}

      {!clientId && (
        <div style={{ ...card, textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
          Pick a client to load their lead feed. {clients.length === 0 && 'No clients have Call Clarity Reports enabled yet.'}
        </div>
      )}

      {clientId && (
        <>
          {/* Section 2: Attribution completion */}
          <div style={card}>
            <h2 style={{ fontSize: 15, marginBottom: 4 }}>Attribution completion</h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              Leads carrying a click ID (gclid / fbclid / msclkid) vs unattributed — the first thing to check after enabling Form Tracking.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Calls — {attribution.call.total} total</div>
                <SplitBar a={attribution.call.matched} b={attribution.call.total - attribution.call.matched} aLabel="Matched" bLabel="Unattributed" />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Forms — {attribution.form.total} total</div>
                {attribution.form.total === 0
                  ? <div style={{ fontSize: 12, color: '#fbbf24', padding: '5px 0' }}>
                      ⚠ No form submissions in this window. If Form Tracking was recently enabled ({client?.businessName}), data starts accumulating from enablement — re-check in a few days. If forms should be flowing, verify the CallScaler script on the site.
                    </div>
                  : <SplitBar a={attribution.form.matched} b={attribution.form.total - attribution.form.matched} aLabel="Matched" bLabel="Unattributed" />}
              </div>
            </div>
          </div>

          {/* Section 3: Channel mix trend */}
          <div style={card}>
            <h2 style={{ fontSize: 15, marginBottom: 4 }}>Channel mix — calls vs forms{windowDays > 31 ? ' (weekly)' : ' (daily)'}</h2>
            <div style={{ display: 'flex', gap: 14, fontSize: 11, color: 'var(--text-secondary)', margin: '6px 0 12px' }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#3730a3', borderRadius: 2, marginRight: 5 }} />Calls ({calls.length})</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--bg-secondary)', border: '1px solid var(--text-muted)', borderRadius: 2, marginRight: 5 }} />Forms ({forms.length})</span>
            </div>
            {trend.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No leads in this window — hit “Refresh lead data” to pull from CallScaler.</div>
              : (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 120, overflowX: 'auto', paddingBottom: 4 }}>
                  {trend.map(b => (
                    <div key={b.key} title={`${b.key}: ${b.calls} calls, ${b.forms} forms`}
                      style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minWidth: 18, flex: 1, height: '100%' }}>
                      <div style={{ height: `${(b.forms / maxTrend) * 100}%`, background: 'var(--bg-secondary)', border: b.forms ? '1px solid var(--text-muted)' : 'none', borderRadius: '2px 2px 0 0' }} />
                      <div style={{ height: `${(b.calls / maxTrend) * 100}%`, background: '#3730a3' }} />
                      <div style={{ fontSize: 9, color: 'var(--text-muted)', textAlign: 'center', marginTop: 3, whiteSpace: 'nowrap' }}>{b.key.slice(5)}</div>
                    </div>
                  ))}
                </div>
              )}
          </div>

          {/* Section 4: Quality cross-tab */}
          <div style={card}>
            <h2 style={{ fontSize: 15, marginBottom: 10 }}>Lead quality</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, cursor: 'default' }}>Segment</th>
                    {QUALITY_ORDER.map(q => <th key={q} style={{ ...th, cursor: 'default' }}>{QUALITY_META[q].label}</th>)}
                    <th style={{ ...th, cursor: 'default' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {qualityByChannel.map(r => (
                    <tr key={r.label}>
                      <td style={{ ...td, fontWeight: 700 }}>{r.label}</td>
                      {QUALITY_ORDER.map(q => (
                        <td key={q} style={td}>
                          {r.counts[q] || 0}
                          {r.total > 0 && r.counts[q] > 0 && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}> ({pct(r.counts[q], r.total)}%)</span>}
                        </td>
                      ))}
                      <td style={{ ...td, fontWeight: 600 }}>{r.total}</td>
                    </tr>
                  ))}
                  {qualityByCampaign.length > 0 && (
                    <tr><td colSpan={QUALITY_ORDER.length + 2} style={{ ...td, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>By campaign / source</td></tr>
                  )}
                  {qualityByCampaign.map(r => (
                    <tr key={r.label}>
                      <td style={td}>{r.label}</td>
                      {QUALITY_ORDER.map(q => <td key={q} style={td}>{r.counts[q] || 0}</td>)}
                      <td style={{ ...td, fontWeight: 600 }}>{r.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sections 5–6 + search terms: Google Ads join (Windsor) */}
          {!client?.windsorAdsAccountId ? (
            <div style={{ ...card, fontSize: 12, color: 'var(--text-muted)' }}>
              No Google Ads account is linked for this client — spend, cost-per-qualified-lead, and claimed-vs-verified are unavailable.
            </div>
          ) : !ads ? (
            <div style={{ ...card, fontSize: 12, color: 'var(--text-muted)' }}>
              Google Ads data hasn't been pulled yet for this client — hit “Refresh lead data” to fetch spend and conversion data via Windsor.
            </div>
          ) : (
            <>
              {!adsWindowMatches && (
                <div style={{ ...card, borderColor: '#fbbf24', color: '#fbbf24', fontSize: 12 }}>
                  Google Ads data below covers {ads.window?.startDate} → {ads.window?.endDate}, not the selected range — hit “Refresh lead data” to update it.
                </div>
              )}

              {/* Section 5: Cost per qualified lead */}
              <div style={card}>
                <h2 style={{ fontSize: 15, marginBottom: 4 }}>Cost per qualified lead</h2>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  Spend and qualified counts both come from Google Ads (the “CallScaler Qualified Calls” conversion action), so campaign rows share one attribution model.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
                  {[
                    ['Ad spend', fmtMoney(ads.totals?.spend)],
                    ['Qualified calls (Google-attributed)', Math.round((ads.totals?.callscalerQualified || 0) * 10) / 10],
                    ['Cost / qualified lead', ads.totals?.costPerQualified != null ? fmtMoney(ads.totals.costPerQualified) : '— (0 qualified)'],
                    ['Claimed conversions', Math.round((ads.totals?.claimedAllConversions || 0) * 10) / 10],
                  ].map(([label, value]) => (
                    <div key={label} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, cursor: 'default' }}>Campaign</th>
                        <th style={{ ...th, cursor: 'default' }}>Spend</th>
                        <th style={{ ...th, cursor: 'default' }}>Claimed conv.</th>
                        <th style={{ ...th, cursor: 'default' }}>Qualified calls</th>
                        <th style={{ ...th, cursor: 'default' }}>Cost / qualified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ads.campaigns || []).map(c => (
                        <tr key={c.campaign}>
                          <td style={td}>{c.campaign}</td>
                          <td style={td}>{fmtMoney(c.spend)}</td>
                          <td style={td}>{c.claimedAllConversions}</td>
                          <td style={{ ...td, fontWeight: c.qualifiedCalls > 0 ? 700 : 400 }}>{c.qualifiedCalls}</td>
                          <td style={{ ...td, fontWeight: 600 }}>{c.costPerQualified != null ? fmtMoney(c.costPerQualified) : <span style={{ color: '#fbbf24' }}>no qualified leads</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Section 6: Claimed vs verified conversions */}
              <div style={card}>
                <h2 style={{ fontSize: 15, marginBottom: 4 }}>Claimed vs verified conversions</h2>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  What Google Ads takes credit for vs what CallScaler verified as a real lead. Attribution windows differ between rows (a qualified call today can credit a click from weeks ago), so expect proximity, not equality.
                </p>
                <CompareBar label="Google claimed — all conversion actions" value={ads.totals?.claimedAllConversions || 0} max={ads.totals?.claimedAllConversions || 1} />
                <CompareBar label="Google claimed — primary conversions" value={ads.totals?.claimedConversions || 0} max={ads.totals?.claimedAllConversions || 1} />
                <CompareBar label="CallScaler Qualified Calls (in Google Ads)" value={ads.totals?.callscalerQualified || 0} max={ads.totals?.claimedAllConversions || 1} strong />
                <CompareBar label="Hub-verified qualified, ad-attributed" value={hubQualified.withGclid} max={ads.totals?.claimedAllConversions || 1} strong note="(has gclid)" />
                <CompareBar label="Hub-verified qualified — all sources" value={hubQualified.total} max={Math.max(ads.totals?.claimedAllConversions || 1, hubQualified.total)} strong note="(incl. organic/direct)" />
                {(ads.conversionActions || []).length > 0 && (
                  <details style={{ marginTop: 10 }}>
                    <summary style={{ fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>What Google is counting ({ads.conversionActions.length} conversion actions)</summary>
                    <table style={{ borderCollapse: 'collapse', marginTop: 8 }}>
                      <tbody>
                        {ads.conversionActions.map(a => (
                          <tr key={a.name}>
                            <td style={{ ...td, fontWeight: /callscaler/i.test(a.name) ? 700 : 400 }}>{a.name}</td>
                            <td style={{ ...td, textAlign: 'right' }}>{a.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )}
              </div>

              {/* Search terms that produced qualified calls */}
              <div style={card}>
                <h2 style={{ fontSize: 15, marginBottom: 4 }}>Search terms that produced qualified calls</h2>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  From Google's search-terms report, filtered to the CallScaler Qualified Calls action. Fractional counts are Google splitting credit across multiple clicks. PMax campaigns don't expose search terms.
                </p>
                {(ads.qualifiedSearchTerms || []).length === 0
                  ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No qualified-call search terms in this window.</div>
                  : (
                    <table style={{ borderCollapse: 'collapse', minWidth: 380 }}>
                      <thead>
                        <tr>
                          <th style={{ ...th, cursor: 'default' }}>Search term</th>
                          <th style={{ ...th, cursor: 'default', textAlign: 'right' }}>Qualified calls</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ads.qualifiedSearchTerms.map(t => (
                          <tr key={t.term}>
                            <td style={td}>{t.term}</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{t.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
              </div>
            </>
          )}

          {/* Section 1: Unified lead feed */}
          <div style={card}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ fontSize: 15, marginRight: 'auto' }}>Lead feed ({feed.length})</h2>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                style={{ padding: '6px 8px', borderRadius: 8, background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', fontSize: 12 }}>
                <option value="">All types</option>
                <option value="call">Calls</option>
                <option value="form">Forms</option>
              </select>
              <select value={qualityFilter} onChange={e => setQualityFilter(e.target.value)}
                style={{ padding: '6px 8px', borderRadius: 8, background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', fontSize: 12 }}>
                <option value="">All quality</option>
                {QUALITY_ORDER.map(q => <option key={q} value={q}>{QUALITY_META[q].label}</option>)}
              </select>
              <input placeholder="Search source / campaign / caller…" value={search} onChange={e => setSearch(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 8, background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', fontSize: 12, minWidth: 220 }} />
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th} onClick={() => toggleSort('timestamp')}>When{sortArrow('timestamp')}</th>
                    <th style={th} onClick={() => toggleSort('leadType')}>Type{sortArrow('leadType')}</th>
                    <th style={th} onClick={() => toggleSort('source')}>Source / Medium{sortArrow('source')}</th>
                    <th style={th} onClick={() => toggleSort('campaign')}>Campaign{sortArrow('campaign')}</th>
                    <th style={{ ...th, cursor: 'default' }}>Attribution</th>
                    <th style={th} onClick={() => toggleSort('qualityScore')}>Quality{sortArrow('qualityScore')}</th>
                    <th style={{ ...th, cursor: 'default' }}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {feed.length === 0 && (
                    <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>
                      No leads match. Pull data with “Refresh lead data” or widen the filters.
                    </td></tr>
                  )}
                  {feed.map(l => (
                    <React.Fragment key={l.id}>
                      <tr onClick={() => setOpenId(openId === l.id ? null : l.id)} style={{ cursor: 'pointer' }}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDateTime(l.timestamp)}</td>
                        <td style={td}>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                            background: l.leadType === 'call' ? '#1e1b4b' : 'var(--bg-secondary)',
                            border: l.leadType === 'call' ? '1px solid #6366f1' : '1px solid var(--border)',
                            color: l.leadType === 'call' ? '#c7d2fe' : 'var(--text-secondary)' }}>
                            {l.leadType === 'call' ? '📞 Call' : '📝 Form'}
                          </span>
                        </td>
                        <td style={td}>{l.source || '—'}{l.medium ? <span style={{ color: 'var(--text-muted)' }}> / {l.medium}</span> : null}</td>
                        <td style={td}>{l.campaign || '—'}{l.keyword ? <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>kw: {l.keyword}</div> : null}</td>
                        <td style={td}><AttributionChip lead={l} /></td>
                        <td style={td}><QualityBadge score={l.qualityScore || 'unscored'} /></td>
                        <td style={{ ...td, fontSize: 12, color: 'var(--text-secondary)' }}>
                          {l.leadType === 'call'
                            ? <>{l.callerName || l.caller || '—'} · {fmtDuration(l.durationSeconds)}{l.aiCategory ? ` · ${l.aiCategory}` : ''}</>
                            : <>{l.formName || 'form'}{l.callerName ? ` · ${l.callerName}` : ''}</>}
                        </td>
                      </tr>
                      {openId === l.id && (
                        <tr>
                          <td colSpan={7} style={{ ...td, background: 'var(--bg-secondary)', fontSize: 12 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 8 }}>
                              {l.qualificationReason && <div><b>Why scored this way:</b> {l.qualificationReason}</div>}
                              {l.landingPage && <div><b>Landing page:</b> <span style={{ wordBreak: 'break-all' }}>{l.landingPage}</span></div>}
                              {l.referrer && <div><b>Referrer:</b> <span style={{ wordBreak: 'break-all' }}>{l.referrer}</span></div>}
                              {l.trackingNumberName && <div><b>Tracking number:</b> {l.trackingNumberName}</div>}
                              {l.formPage && <div><b>Form page:</b> <span style={{ wordBreak: 'break-all' }}>{l.formPage}</span></div>}
                              {l.matchedConversion != null && (
                                <div><b>Pushed to Google Ads:</b> {l.matchedConversion ? 'Yes — qualified + gclid (derived)' : 'No'}</div>
                              )}
                              <div><b>CallScaler record:</b> {l.rawSourceId}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
